import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  FolderKanban,
  AlertTriangle,
  CheckCircle2,
  User,
  ExternalLink,
  MessageSquare,
  Paperclip,
  Send,
  Sparkles,
  Flag,
  FileText,
  X,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { useCases, useCase, useCreateCase, useResolveCase, useAddCaseNote, useCaseCounts } from '../hooks/useCases';
import { useTestMode } from '../contexts/TestModeContext';
import { usePlaybooks } from '../hooks/usePlaybooks';
import SarModal from './cases/SarModal';
import { useRiskSummary } from '../hooks/useAI';
import { useApplicants, useApplicant } from '../hooks/useApplicants';
import { useToast } from '../contexts/ToastContext';
import { Button, Badge, Card, Spinner } from './ui';
import { cn } from '../lib/utils';
import {
  CASE_TYPES as typeConfig,
  CASE_PRIORITIES as priorityConfig,
  CASE_STATUSES as statusConfig,
  formatDateTime,
  formatRelativeTime,
} from '../config/caseConfig';

// Inline Modal components (matching AlertsDashboard pattern)
function Modal({ isOpen, onClose, children }) {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000]"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-2xl w-full max-w-[500px] max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ children, onClose }) {
  return (
    <div className="flex justify-between items-center p-5 border-b border-grey-200 dark:border-grey-800">
      <h2 className="text-lg font-semibold m-0">{children}</h2>
      {onClose && (
        <button
          className="bg-transparent border-none text-grey-400 cursor-pointer p-1 rounded hover:bg-grey-100 dark:hover:bg-grey-800 hover:text-grey-900 dark:hover:text-grey-50"
          onClick={onClose}
        >
          <X size={20} />
        </button>
      )}
    </div>
  );
}

function ModalFooter({ children }) {
  return (
    <div className="flex justify-end gap-3 p-4 border-t border-grey-200 dark:border-grey-800 mt-2">
      {children}
    </div>
  );
}

// Create Case Modal Component
function CreateCaseModal({ isOpen, onClose, onSubmit, isSubmitting, initialApplicant }) {
  const [formData, setFormData] = useState({
    title: '',
    type: 'verification',
    priority: 'medium',
    applicant_id: '',
    playbook_id: '',
    description: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  // Fetch available playbooks
  const { data: playbooks = [] } = usePlaybooks();

  // Pre-populate from initialApplicant when modal opens
  useEffect(() => {
    if (isOpen && initialApplicant) {
      setFormData(prev => ({ ...prev, applicant_id: initialApplicant.id }));
      setSearchTerm(`${initialApplicant.first_name || ''} ${initialApplicant.last_name || ''}`.trim());
    }
  }, [isOpen, initialApplicant]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ title: '', type: 'verification', priority: 'medium', applicant_id: '', playbook_id: '', description: '' });
      setSearchTerm('');
      setDebouncedSearch('');
    }
  }, [isOpen]);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm]);

  // Only fetch when debounced search has 2+ chars and no applicant is already selected
  const { data: applicantsData } = useApplicants(
    { search: debouncedSearch, limit: 10 },
    { enabled: debouncedSearch.length >= 2 && !formData.applicant_id }
  );
  const applicants = applicantsData?.items || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader onClose={onClose}>Create New Case</ModalHeader>
      <form onSubmit={handleSubmit} className="p-5">
        <div className="mb-4">
          <label className="block text-[13px] font-medium mb-1.5 text-grey-600 dark:text-grey-400">Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Case title..."
            required
            className="w-full py-2.5 px-3 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg text-sm text-grey-900 dark:text-grey-50 font-inherit outline-none focus:border-teal-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="mb-4">
            <label className="block text-[13px] font-medium mb-1.5 text-grey-600 dark:text-grey-400">Type</label>
            <select
              value={formData.type}
              onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full py-2.5 px-3 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg text-sm text-grey-900 dark:text-grey-50 font-inherit outline-none focus:border-teal-500"
            >
              <option value="verification">Verification</option>
              <option value="sanctions">Sanctions</option>
              <option value="pep">PEP</option>
              <option value="fraud">Fraud</option>
              <option value="aml">AML</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-[13px] font-medium mb-1.5 text-grey-600 dark:text-grey-400">Priority</label>
            <select
              value={formData.priority}
              onChange={e => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full py-2.5 px-3 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg text-sm text-grey-900 dark:text-grey-50 font-inherit outline-none focus:border-teal-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-[13px] font-medium mb-1.5 text-grey-600 dark:text-grey-400">Link to Applicant</label>
          <input
            type="text"
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              // Clear selection when user edits the search
              if (formData.applicant_id) {
                setFormData(prev => ({ ...prev, applicant_id: '' }));
              }
            }}
            placeholder="Search applicants..."
            className="w-full py-2.5 px-3 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg text-sm text-grey-900 dark:text-grey-50 font-inherit outline-none focus:border-teal-500"
          />
          {applicants.length > 0 && searchTerm && !formData.applicant_id && (
            <div className="bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg mt-1 max-h-[150px] overflow-y-auto">
              {applicants.map(a => (
                <div
                  key={a.id}
                  className={cn(
                    'py-2.5 px-3 cursor-pointer text-[13px] border-b border-grey-200 dark:border-grey-700 last:border-b-0 hover:bg-grey-100 dark:hover:bg-grey-700',
                    formData.applicant_id === a.id && 'bg-teal-50 dark:bg-teal-950/50 text-teal-600 dark:text-teal-400'
                  )}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, applicant_id: a.id }));
                    setSearchTerm(`${a.first_name} ${a.last_name}`);
                  }}
                >
                  {a.first_name} {a.last_name} - {a.email}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-[13px] font-medium mb-1.5 text-grey-600 dark:text-grey-400">Investigation Playbook</label>
          <select
            value={formData.playbook_id}
            onChange={e => setFormData(prev => ({ ...prev, playbook_id: e.target.value }))}
            className="w-full py-2.5 px-3 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg text-sm text-grey-900 dark:text-grey-50 font-inherit outline-none focus:border-teal-500"
          >
            <option value="">No playbook (manual workflow)</option>
            {playbooks.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.checklist_item_count} items)
              </option>
            ))}
          </select>
          {formData.playbook_id && (
            <div className="mt-1.5 text-xs text-grey-400 italic">
              {playbooks.find(p => p.id === formData.playbook_id)?.description ||
                'Playbook will guide the investigation with a checklist.'}
            </div>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-[13px] font-medium mb-1.5 text-grey-600 dark:text-grey-400">Description</label>
          <textarea
            value={formData.description}
            onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Case description..."
            rows={3}
            className="w-full py-2.5 px-3 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg text-sm text-grey-900 dark:text-grey-50 font-inherit outline-none focus:border-teal-500 resize-none"
          />
        </div>

        <ModalFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || !formData.title}>
            {isSubmitting ? <><Loader2 size={16} className="animate-spin" /> Creating...</> : 'Create Case'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// AI Summary Component for Case Detail
function CaseAISummary({ applicantId, caseType }) {
  const { data: riskSummary, isLoading, error } = useRiskSummary(applicantId);

  if (!applicantId) {
    // Fallback to type-based summary if no applicant linked
    const typeBasedSummaries = {
      sanctions: "This case involves a potential OFAC SDN match. Recommend verifying identity documents and requesting additional proof of identity before disposition.",
      pep: "PEP match identified. Standard enhanced due diligence should be completed to assess risk profile.",
      verification: "Verification check requires manual review. Please examine the flagged documents or liveness results.",
      fraud: "Potential fraud indicators detected. Review all submitted documents for signs of manipulation.",
      aml: "AML screening hit requires investigation. Review transaction history and source of funds."
    };

    return (
      <Card className="border-teal-500/50 bg-gradient-to-br from-white to-teal-500/5 dark:from-grey-900 dark:to-teal-500/10 p-4">
        <div className="flex items-center gap-2 font-semibold text-teal-600 dark:text-teal-400 mb-2 text-[13px]">
          <Sparkles size={14} />
          AI Analysis
        </div>
        <div className="text-[13px] leading-relaxed text-grey-900 dark:text-grey-50">
          {typeBasedSummaries[caseType] || "AI analysis pending. Link an applicant to see detailed risk assessment."}
        </div>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="border-grey-200 dark:border-grey-700 bg-grey-50 dark:bg-grey-800 p-4">
        <div className="flex items-center gap-2 font-semibold text-grey-500 mb-2 text-[13px]">
          <Loader2 size={14} className="animate-spin" />
          Loading AI Analysis...
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-grey-200 dark:border-grey-700 bg-grey-50 dark:bg-grey-800 p-4">
        <div className="flex items-center gap-2 font-semibold text-red-500 mb-2 text-[13px]">
          <AlertTriangle size={14} />
          Analysis Unavailable
        </div>
        <div className="text-[13px] leading-relaxed text-grey-600 dark:text-grey-400">
          Unable to load AI analysis. Please try again later.
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-teal-500/50 bg-gradient-to-br from-white to-teal-500/5 dark:from-grey-900 dark:to-teal-500/10 p-4">
      <div className="flex items-center gap-2 font-semibold text-teal-600 dark:text-teal-400 mb-2 text-[13px]">
        <Sparkles size={14} />
        AI Analysis
        {riskSummary?.generated_at && (
          <span className="ml-auto font-normal text-[11px] text-grey-400">
            {new Date(riskSummary.generated_at).toLocaleDateString()}
          </span>
        )}
      </div>
      <div className="text-[13px] leading-relaxed text-grey-900 dark:text-grey-50">
        {riskSummary?.summary || riskSummary?.analysis || "No AI summary available for this applicant."}
      </div>
      {riskSummary?.risk_score && (
        <div className="mt-3 pt-3 border-t border-grey-200 dark:border-grey-700 text-[13px] text-grey-600 dark:text-grey-400">
          Risk Score: <strong>{riskSummary.risk_score}/100</strong>
        </div>
      )}
    </Card>
  );
}

// Priority badge variant mapping
const priorityVariants = {
  critical: 'destructive',
  high: 'warning',
  medium: 'secondary',
  low: 'outline',
};

// Status badge variant mapping
const statusVariants = {
  open: 'warning',
  in_progress: 'info',
  resolved: 'success',
  escalated: 'destructive',
};

// Type badge variant mapping
const typeVariants = {
  sanctions: 'destructive',
  fraud: 'destructive',
  pep: 'warning',
  aml: 'warning',
  verification: 'info',
};

export default function CaseManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState('open');
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSarModal, setShowSarModal] = useState(false);

  // Read URL params for auto-opening case creation
  const urlApplicantId = searchParams.get('applicant_id');
  const urlCreate = searchParams.get('create');

  // Fetch the applicant if provided via URL
  const { data: urlApplicantData } = useApplicant(urlApplicantId);

  // Auto-open create modal when URL params indicate it
  useEffect(() => {
    if (urlCreate === 'true') {
      setShowCreateModal(true);
      // Clear URL params after opening
      setSearchParams({}, { replace: true });
    }
  }, [urlCreate, setSearchParams]);

  // Toast notifications
  const toast = useToast();

  // Environment
  const { isTestMode } = useTestMode();

  // API hooks
  const statusFilter = activeFilter === 'all' ? {} : { status: activeFilter };
  const { data: casesData, isLoading, error, refetch } = useCases({ ...statusFilter, search: searchQuery, is_sandbox: isTestMode });
  const { data: counts } = useCaseCounts({ is_sandbox: isTestMode });
  const { data: selectedCaseData } = useCase(selectedCaseId);

  const createCaseMutation = useCreateCase();
  const resolveCaseMutation = useResolveCase();
  const addNoteMutation = useAddCaseNote();

  const cases = casesData?.items || [];
  const selectedCase = selectedCaseData || cases.find(c => c.id === selectedCaseId);

  const handleCreateCase = async (formData) => {
    try {
      await createCaseMutation.mutateAsync(formData);
      setShowCreateModal(false);
      toast.success('Case created successfully');
    } catch (err) {
      console.error('Failed to create case:', err);
      toast.error(`Failed to create case: ${err.message}`);
    }
  };

  const handleResolve = async (resolution) => {
    if (!selectedCaseId) return;
    try {
      await resolveCaseMutation.mutateAsync({
        id: selectedCaseId,
        resolution,
        notes: null
      });
      setSelectedCaseId(null);
      const resolutionLabel = resolution === 'cleared' ? 'cleared' : 'escalated';
      toast.success(`Case ${resolutionLabel} successfully`);
    } catch (err) {
      console.error('Failed to resolve case:', err);
      toast.error(`Failed to resolve case: ${err.message}`);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !selectedCaseId) return;
    try {
      await addNoteMutation.mutateAsync({
        id: selectedCaseId,
        content: noteText
      });
      setNoteText('');
      toast.success('Note added');
    } catch (err) {
      console.error('Failed to add note:', err);
      toast.error(`Failed to add note: ${err.message}`);
    }
  };

  return (
    <div className="max-w-[1400px]" data-testid="page-cases" data-page-ready={!isLoading} data-page-loading={isLoading}>
      {/* Create Case Modal */}
      <CreateCaseModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateCase}
        isSubmitting={createCaseMutation.isPending}
        initialApplicant={urlApplicantData || null}
      />

      {/* Page Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-[28px] font-semibold tracking-tight">Case Management</h1>
          <p className="text-grey-500 text-sm mt-1">Investigate and resolve compliance cases</p>
        </div>

        <div className="flex gap-3">
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus size={16} />
            Create Case
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <Card
          className={cn(
            'p-4 cursor-pointer transition-all hover:border-teal-500',
            activeFilter === 'open' && 'border-teal-500 bg-teal-50/50 dark:bg-teal-950/20'
          )}
          onClick={() => setActiveFilter('open')}
        >
          <div className="text-[13px] text-grey-500 mb-1">Open Cases</div>
          <div className="text-[28px] font-bold tracking-tight text-orange-500">{counts?.open ?? '-'}</div>
        </Card>
        <Card
          className={cn(
            'p-4 cursor-pointer transition-all hover:border-teal-500',
            activeFilter === 'in_progress' && 'border-teal-500 bg-teal-50/50 dark:bg-teal-950/20'
          )}
          onClick={() => setActiveFilter('in_progress')}
        >
          <div className="text-[13px] text-grey-500 mb-1">In Progress</div>
          <div className="text-[28px] font-bold tracking-tight text-blue-500">{counts?.in_progress ?? '-'}</div>
        </Card>
        <Card
          className={cn(
            'p-4 cursor-pointer transition-all hover:border-teal-500',
            activeFilter === 'resolved' && 'border-teal-500 bg-teal-50/50 dark:bg-teal-950/20'
          )}
          onClick={() => setActiveFilter('resolved')}
        >
          <div className="text-[13px] text-grey-500 mb-1">Resolved (30d)</div>
          <div className="text-[28px] font-bold tracking-tight text-green-500">{counts?.resolved ?? '-'}</div>
        </Card>
        <Card
          className={cn(
            'p-4 cursor-pointer transition-all hover:border-teal-500',
            activeFilter === 'escalated' && 'border-teal-500 bg-teal-50/50 dark:bg-teal-950/20'
          )}
          onClick={() => setActiveFilter('escalated')}
        >
          <div className="text-[13px] text-grey-500 mb-1">Escalated</div>
          <div className="text-[28px] font-bold tracking-tight text-red-500">{counts?.escalated ?? 0}</div>
        </Card>
        <Card
          className={cn(
            'p-4 cursor-pointer transition-all hover:border-teal-500',
            activeFilter === 'all' && 'border-teal-500 bg-teal-50/50 dark:bg-teal-950/20'
          )}
          onClick={() => setActiveFilter('all')}
        >
          <div className="text-[13px] text-grey-500 mb-1">All Cases</div>
          <div className="text-[28px] font-bold tracking-tight">{counts?.total ?? cases.length}</div>
        </Card>
      </div>

      {/* Cases Container */}
      <div className="grid grid-cols-[1fr_400px] gap-6">
        {/* Cases List */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-grey-200 dark:border-grey-800 flex items-center gap-3">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-grey-400" />
              <input
                type="text"
                className="w-full h-10 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg pl-10 pr-3 text-sm text-grey-900 dark:text-grey-50 outline-none focus:border-teal-500"
                placeholder="Search cases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </Button>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16 text-grey-400">
              <Spinner size="lg" />
              <div className="mt-3">Loading cases...</div>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-10 text-center text-red-500">
              <AlertTriangle size={32} />
              <div className="mt-3">Failed to load cases</div>
              <Button variant="secondary" className="mt-4" onClick={() => refetch()}>
                Try Again
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && cases.length === 0 && (
            <div className="py-10 text-center text-grey-400">
              <FolderKanban size={32} className="mx-auto mb-3 opacity-50" />
              <div>No cases found</div>
              <div className="text-[13px] mt-1">
                {searchQuery ? 'Try a different search term' : 'Create a new case to get started'}
              </div>
            </div>
          )}

          {/* Cases List */}
          {!isLoading && !error && cases.map((caseItem) => {
            const type = typeConfig[caseItem.type];
            const priority = priorityConfig[caseItem.priority];
            const status = statusConfig[caseItem.status];
            const TypeIcon = type?.icon;

            // Handle both API format and display format
            const caseId = caseItem.case_number || caseItem.id;
            const lastActivity = caseItem.updated_at || caseItem.lastActivity || caseItem.created_at;
            const noteCount = caseItem.notes?.length || caseItem.note_count || 0;
            const attachmentCount = caseItem.attachments?.length || caseItem.attachment_count || 0;

            return (
              <div
                key={caseItem.id}
                className={cn(
                  'p-4 border-b border-grey-200 dark:border-grey-800 last:border-b-0 cursor-pointer transition-colors hover:bg-grey-50 dark:hover:bg-grey-800/50',
                  selectedCaseId === caseItem.id && 'bg-teal-50/50 dark:bg-teal-950/20 border-l-[3px] border-l-teal-500'
                )}
                onClick={() => setSelectedCaseId(caseItem.id)}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs text-grey-400 font-mono">{caseId}</span>
                  <span className="text-xs text-grey-400">{formatRelativeTime(lastActivity)}</span>
                </div>
                <div className="text-sm font-medium mb-2 leading-snug">{caseItem.title}</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={typeVariants[caseItem.type] || 'secondary'} size="sm">
                    {TypeIcon && <TypeIcon size={10} />}
                    {type?.label || caseItem.type}
                  </Badge>
                  <Badge variant={priorityVariants[caseItem.priority] || 'secondary'} size="sm">
                    {priority?.label || caseItem.priority}
                  </Badge>
                  <Badge variant={statusVariants[caseItem.status] || 'secondary'} size="sm">
                    {status?.label || caseItem.status}
                  </Badge>
                </div>
                <div className="flex justify-between mt-3">
                  <div className="flex items-center gap-1.5 text-xs text-grey-500">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-[9px] font-semibold text-white">
                      {caseItem.assigned_to ? (caseItem.assigned_to.name?.[0] || 'U') : 'RT'}
                    </div>
                    {caseItem.assigned_to?.name || 'Unassigned'}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-grey-400">
                    <span className="flex items-center gap-1">
                      <MessageSquare size={12} />
                      {noteCount}
                    </span>
                    <span className="flex items-center gap-1">
                      <Paperclip size={12} />
                      {attachmentCount}
                    </span>
                    <button
                      className="flex items-center justify-center p-1 bg-grey-100 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded text-grey-500 hover:bg-teal-500 hover:border-teal-500 hover:text-white transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/cases/${caseItem.id}`);
                      }}
                      title="Open full case"
                    >
                      <ExternalLink size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </Card>

        {/* Case Detail Panel */}
        <Card className="flex flex-col max-h-[calc(100vh-200px)]">
          {selectedCase ? (
            <>
              <div className="p-5 border-b border-grey-200 dark:border-grey-800">
                <div className="text-xs text-grey-400 font-mono mb-2">
                  {selectedCase.case_number || selectedCase.id}
                </div>
                <div className="text-base font-semibold mb-3 leading-snug">{selectedCase.title}</div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={typeVariants[selectedCase.type] || 'secondary'}>
                    {typeConfig[selectedCase.type]?.label || selectedCase.type}
                  </Badge>
                  <Badge variant={priorityVariants[selectedCase.priority] || 'secondary'}>
                    {priorityConfig[selectedCase.priority]?.label || selectedCase.priority}
                  </Badge>
                  <Badge variant={statusVariants[selectedCase.status] || 'secondary'}>
                    {statusConfig[selectedCase.status]?.label || selectedCase.status}
                  </Badge>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                {/* Subject Section */}
                <div className="mb-6">
                  <div className="text-xs font-semibold text-grey-400 uppercase tracking-wider mb-3">Subject</div>
                  {selectedCase.applicant_id || selectedCase.applicant ? (
                    <div
                      className="bg-grey-50 dark:bg-grey-800 rounded-lg p-3 flex items-center gap-3 cursor-pointer transition-colors hover:bg-grey-100 dark:hover:bg-grey-700"
                      onClick={() => navigate(`/applicants/${selectedCase.applicant_id || selectedCase.applicant?.id}`)}
                      title="View applicant profile"
                    >
                      <div className="w-10 h-10 rounded-lg bg-grey-100 dark:bg-grey-700 flex items-center justify-center text-grey-500">
                        <User size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {selectedCase.applicant?.first_name
                            ? `${selectedCase.applicant.first_name} ${selectedCase.applicant.last_name}`
                            : 'Linked Applicant'}
                        </div>
                        <div className="text-xs text-grey-400 font-mono">
                          {selectedCase.applicant_id || selectedCase.applicant?.id}
                        </div>
                      </div>
                      <ExternalLink size={16} className="text-teal-500" />
                    </div>
                  ) : (
                    <div className="bg-grey-50 dark:bg-grey-800 rounded-lg p-3 flex items-center gap-3 text-grey-400">
                      <div className="w-10 h-10 rounded-lg bg-grey-100 dark:bg-grey-700 flex items-center justify-center">
                        <User size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">No linked applicant</div>
                        <div className="text-xs">Case created manually</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* AI Summary Section */}
                <div className="mb-6">
                  <div className="text-xs font-semibold text-grey-400 uppercase tracking-wider mb-3">AI Summary</div>
                  <CaseAISummary
                    applicantId={selectedCase.applicant_id || selectedCase.applicant?.id}
                    caseType={selectedCase.type}
                  />
                </div>

                {/* Notes Section */}
                <div className="mb-6">
                  <div className="text-xs font-semibold text-grey-400 uppercase tracking-wider mb-3">
                    Notes ({selectedCase.notes?.length || 0})
                  </div>
                  {selectedCase.notes && selectedCase.notes.length > 0 ? (
                    selectedCase.notes.map((note, idx) => (
                      <div key={note.id || idx} className="bg-grey-50 dark:bg-grey-800 rounded-lg p-3 mb-2">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-[10px] font-semibold text-white">
                            {note.created_by?.name?.[0] || note.author?.[0] || '?'}
                          </div>
                          <span className="font-medium text-[13px]">
                            {note.created_by?.name || note.author || 'Unknown'}
                          </span>
                          <span className="text-[11px] text-grey-400">
                            {formatRelativeTime(note.created_at)}
                          </span>
                        </div>
                        <div className="text-[13px] leading-snug text-grey-600 dark:text-grey-400">{note.content}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-grey-400 text-[13px]">
                      No notes yet. Add a note below.
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                {selectedCase.status !== 'resolved' && (
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="success"
                      className="flex-1"
                      onClick={() => handleResolve('cleared')}
                      disabled={resolveCaseMutation.isPending}
                    >
                      {resolveCaseMutation.isPending ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <CheckCircle2 size={14} />
                      )}
                      Clear
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => handleResolve('escalated')}
                      disabled={resolveCaseMutation.isPending}
                    >
                      {resolveCaseMutation.isPending ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Flag size={14} />
                      )}
                      Escalate
                    </Button>
                    <Button
                      variant="secondary"
                      className="flex-1"
                      onClick={() => setShowSarModal(true)}
                    >
                      <FileText size={14} />
                      File SAR
                    </Button>
                  </div>
                )}

                {/* Show resolution info for resolved cases */}
                {selectedCase.status === 'resolved' && (
                  <div className="bg-grey-50 dark:bg-grey-800 p-4 rounded-lg mt-4">
                    <div className="text-xs text-grey-400 mb-1">
                      Resolution
                    </div>
                    <div className="text-sm font-medium">
                      {selectedCase.resolution === 'cleared' ? '✓ Cleared' :
                       selectedCase.resolution === 'escalated' ? '⚠ Escalated' :
                       selectedCase.resolution || 'Resolved'}
                    </div>
                    {selectedCase.resolved_at && (
                      <div className="text-xs text-grey-400 mt-1">
                        {formatDateTime(selectedCase.resolved_at)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-grey-200 dark:border-grey-800">
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 h-10 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg px-3 text-sm text-grey-900 dark:text-grey-50 outline-none focus:border-teal-500"
                    placeholder="Add a note..."
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                    disabled={addNoteMutation.isPending}
                  />
                  <button
                    className="w-10 h-10 rounded-lg border-none bg-teal-500 text-white cursor-pointer flex items-center justify-center disabled:opacity-50"
                    onClick={handleAddNote}
                    disabled={!noteText.trim() || addNoteMutation.isPending}
                  >
                    {addNoteMutation.isPending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-10 text-center text-grey-400">
              <div className="w-16 h-16 rounded-2xl bg-grey-100 dark:bg-grey-800 flex items-center justify-center mb-4">
                <FolderKanban size={28} />
              </div>
              <div className="text-sm font-medium mb-1">No case selected</div>
              <div className="text-[13px]">Select a case from the list to view details</div>
            </div>
          )}
        </Card>
      </div>

      <SarModal
        isOpen={showSarModal}
        onClose={() => setShowSarModal(false)}
        caseData={selectedCase}
      />
    </div>
  );
}
